import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../../i18n";
import { services, LOGO_URL } from "../../data/services";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";

const LABELS = {
  fr: { services: "Services", expertise: "Expertise", realisations: "Réalisations", contact: "Contact" },
  en: { services: "Services", expertise: "Expertise", realisations: "Projects", contact: "Contact" },
};

export const Navbar = () => {
  const { lang, setLang } = useLang();
  const L = LABELS[lang];
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropdown, setDropdown] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = ({ isActive }) =>
    `font-mono-p text-sm relative transition-colors duration-300 ${isActive ? "text-primary-c" : "text-white/70 hover:text-white"}`;

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-[background,backdrop-filter,border-color] duration-500 ${scrolled ? "bg-[rgba(5,5,6,0.75)] backdrop-blur-xl border-b border-white/10" : "border-b border-transparent"}`} data-testid="navbar">
      <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center">
          <img src={LOGO_URL} alt="Fixedrone" className="h-7 md:h-8 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <div className="relative" onMouseEnter={() => setDropdown(true)} onMouseLeave={() => setDropdown(false)}>
            <NavLink to="/services" className={linkCls} data-testid="nav-services">
              <span className="flex items-center gap-1">{L.services} <ChevronDown size={14} /></span>
            </NavLink>
            <AnimatePresence>
              {dropdown && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-72">
                  <div className="bg-[rgba(10,10,12,0.95)] backdrop-blur-xl border border-white/10 rounded-xl p-2 shadow-2xl">
                    {services.map((s) => (
                      <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`nav-service-${s.slug}`}
                        className="block px-4 py-2.5 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors">
                        {s[lang].name}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <NavLink to="/expertise" className={linkCls} data-testid="nav-expertise">{L.expertise}</NavLink>
          <NavLink to="/realisations" className={linkCls} data-testid="nav-realisations">{L.realisations}</NavLink>
          <NavLink to="/contact" className={linkCls} data-testid="nav-contact">{L.contact}</NavLink>
        </nav>

        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-1 font-mono-p text-sm border border-white/15 rounded-full p-1">
            {["fr", "en"].map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}
                className={`px-3 py-1 rounded-full transition-colors duration-300 ${lang === l ? "bg-[var(--primary)] text-black font-semibold" : "text-white/60 hover:text-white"}`}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <Link to="/contact" data-testid="nav-cta" className="hidden md:inline-flex items-center gap-1.5 font-mono-p text-sm px-5 py-2.5 rounded-full bg-white text-black hover:bg-[var(--primary)] transition-colors duration-300">
            {lang === "fr" ? "Devis gratuit" : "Free quote"} <ArrowUpRight size={15} />
          </Link>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-[rgba(5,5,6,0.97)] backdrop-blur-xl border-b border-white/10">
            <div className="px-6 py-6 flex flex-col gap-4">
              <Link to="/services" onClick={() => setOpen(false)} className="font-display text-2xl">{L.services}</Link>
              <div className="pl-4 flex flex-col gap-2 border-l border-white/10">
                {services.map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setOpen(false)} className="text-white/60 text-sm" data-testid={`mnav-service-${s.slug}`}>{s[lang].name}</Link>
                ))}
              </div>
              <Link to="/expertise" onClick={() => setOpen(false)} className="font-display text-2xl">{L.expertise}</Link>
              <Link to="/realisations" onClick={() => setOpen(false)} className="font-display text-2xl">{L.realisations}</Link>
              <Link to="/contact" onClick={() => setOpen(false)} className="font-display text-2xl">{L.contact}</Link>
              <div className="flex gap-2 pt-2">
                {["fr", "en"].map((l) => (
                  <button key={l} onClick={() => setLang(l)} className={`px-4 py-2 rounded-full font-mono-p text-sm ${lang === l ? "bg-[var(--primary)] text-black" : "border border-white/20"}`}>{l.toUpperCase()}</button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
